import React, { useState } from 'react';
import { TouchableWithoutFeedback, View } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { get as _get } from 'lodash';
import PropTypes from 'prop-types';

import { Icon, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import Accordion from '@vfgroup-oneplatform/foundation/Components/Timeline/Accordion/Accordion';
import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager';
import { DateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils';

import TotalCostSection from '../../../CommonUI/TotalCostSection';
import WhyFirstBillModal from '../../../CommonUI/WhyFirstBillModal';
import { getThemeImages } from '../../../Themes';
import CostBreakDown from '../CostBreakDown';

import styles from './TotalCostSection.Styles';

const { currencyTransformer } = DateCurrencyFromatter.dateCurrencyFromatter;
const renderBreakDownHeader = (isBreakDownExpanded, theme) => () => {
  const images = getThemeImages(theme.name);
  return (
    <View style={styles.breakDownHeaderContainer}>
      <VFText
        type={'primary'}
        style={styles.sectionTitle}
        i18nKey={'device_upgrade_summary_step_total_cost_breakdown'}
      />
      <Icon
        size={30}
        name={
          isBreakDownExpanded
            ? images.TImages.ic_chevron_up.primary
            : images.TImages.ic_chevron_down.primary
        }
      />
    </View>
  );
};
const renderBreakDownContent = (selectedDevice, selectedPlan) => () => {
  const currency = _get(selectedDevice, 'price.currency', '');
  const recurringChargePeriodDuration = _get(
    selectedDevice,
    'price.recurringChargePeriodDuration',
    0,
  );
  return (
    <CostBreakDown
      planImage={selectedDevice?.image}
      recurringChargePeriodDuration={recurringChargePeriodDuration}
      currency={currency}
      selectedDevice={selectedDevice}
      selectedPlan={selectedPlan}
    />
  );
};
const renderAfterSubTitle = (selectedDevice, planRecurringChargePrice) => {
  const deviceUpfrontPrice = _get(selectedDevice, 'price.upfront', 0);
  const deviceMonthlyPrice = _get(selectedDevice, 'price.recurringCharge', 0);
  const monthlyPrice = deviceMonthlyPrice + planRecurringChargePrice;
  const currency = selectedDevice?.price?.currency;
  return (
    <View style={styles.headerContainer}>
      <VFText
        localize={false}
        style={styles.price}
        i18nKey={currencyTransformer(deviceUpfrontPrice, 0) + currency + ' '}
      />
      <VFText
        type={'primary'}
        style={styles.upfront}
        i18nKey={'device_upgrade_summary_step_total_cost_upfront'}
      />
      <VFText
        localize={false}
        style={styles.price}
        i18nKey={currencyTransformer(monthlyPrice, 0) + currency + ' '}
      />
      <VFText
        type={'primary'}
        style={styles.monthly}
        i18nKey={'device_upgrade_summary_step_total_cost_monthly'}
      />
    </View>
  );
};
const renderBreakDownSection = (
  isBreakDownExpanded,
  setIsBreakDownExpanded,
  selectedDevice,
  selectedPlan,
  theme,
) => {
  return (
    <Accordion
      containerStyle={styles.breakDownContainer}
      touchableComponent={TouchableWithoutFeedback}
      activeSections={isBreakDownExpanded ? [0] : []}
      sections={['']}
      onSectionContainerLayout={() => {}}
      onChange={() => setIsBreakDownExpanded(!isBreakDownExpanded)}
      renderHeader={renderBreakDownHeader(isBreakDownExpanded, theme)}
      renderContent={renderBreakDownContent(selectedDevice, selectedPlan)}
    />
  );
};
const renderFirstBill = (firstBill, setIsWhyFirstBillModalVisible) => (
  <VFText>
    <VFText
      localize={false}
      style={styles.infoValue}
      type={'primary'}
      i18nKey={`${currencyTransformer(firstBill, 0)} `}
    />
    <TouchableWithoutFeedback onPress={() => setIsWhyFirstBillModalVisible(true)}>
      <VFText
        style={styles.whyText}
        type={'tertiary'}
        i18nKey={'device_upgrade_summary_step_total_cost_first_bill_why'}
      />
    </TouchableWithoutFeedback>
  </VFText>
);
const TotalCostSectionComponent = ({
  selectedDevice,
  selectedPlan,
  onTermsAndConditionsPress,
  onReturnPolicyPress,
  onConfirmPress,
  firstBill,
  startDate,
  containerStyle,
}) => {
  const [isBreakDownExpanded, setIsBreakDownExpanded] = useState(false);
  const [isWhyFirstBillModalVisible, setIsWhyFirstBillModalVisible] = useState(false);
  const theme = useTheme();

  const sections = [
    [
      {
        key: 'device_upgrade_summary_step_total_cost_start_date',
        value: startDate,
      },
      {
        key: 'device_upgrade_summary_step_total_cost_duration',
        value: ContentManager.translate('device_upgrade_summary_step_total_cost_duration_value', [
          selectedDevice?.price?.recurringChargePeriodDuration,
        ]),
      },
      {
        key: 'device_upgrade_summary_step_total_cost_first_bill',
        renderValue: () => renderFirstBill(firstBill, setIsWhyFirstBillModalVisible),
      },
    ],
    [
      {
        renderSection: () =>
          renderBreakDownSection(
            isBreakDownExpanded,
            setIsBreakDownExpanded,
            selectedDevice,
            selectedPlan,
            theme,
          ),
      },
    ],
  ];
  const termsAndConditionsProps = {
    note: 'device_upgrade_summary_step_terms_note',
    localizeKey: 'device_upgrade_summary_step_see_terms',
    hyperlinksKeys: [
      'device_upgrade_summary_step_privacy_hyperlink_text',
      'device_upgrade_summary_step_terms_hyperlink_text',
    ],
    onTermsAndConditionsPress: onTermsAndConditionsPress,
    onReturnPolicyPress: onReturnPolicyPress,
  };
  return (
    <>
      <TotalCostSection
        title={'device_upgrade_summary_step_total_cost_title'}
        subTitle={'device_upgrade_summary_step_total_cost_note'}
        renderAfterSubTitle={() => renderAfterSubTitle(selectedDevice, selectedPlan?.price)}
        sections={sections}
        primaryButtonTitle={'device_upgrade_summary_step_cta_button_title'}
        onPrimaryButtonPress={onConfirmPress}
        termsAndConditionsProps={termsAndConditionsProps}
        containerStyle={containerStyle}
      />
      <WhyFirstBillModal
        titleKey={'device_upgrade_summary_step_total_cost_first_bill_why_modal_title'}
        questionKey={'device_upgrade_summary_step_total_cost_first_bill_why_modal_question'}
        descriptionKey={'device_upgrade_summary_step_total_cost_first_bill_why_modal_description'}
        buttonKey={'device_upgrade_summary_step_total_cost_first_bill_why_modal_button_title'}
        firstBill={firstBill}
        isVisible={isWhyFirstBillModalVisible}
        closeModal={() => setIsWhyFirstBillModalVisible(false)}
        onClosePress={() => setIsWhyFirstBillModalVisible(false)}
      />
    </>
  );
};
TotalCostSectionComponent.propTypes = {
  selectedDevice: PropTypes.object,
  selectedPlan: PropTypes.object,
  onTermsAndConditionsPress: PropTypes.func,
  onReturnPolicyPress: PropTypes.func,
  onConfirmPress: PropTypes.func,
  firstBill: PropTypes.string,
  startDate: PropTypes.string,
  containerStyle: ViewPropTypes.style,
};
export default TotalCostSectionComponent;
